import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { API, graphqlOperation } from 'aws-amplify'
import { listOrders, listMarkets } from '../graphql/queries'
import { Loading } from 'element-react'
import config from '../aws-exports'

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket,
} = config

const dollars = cents =>
  '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatNumber = num => num.toLocaleString('en-US')

const SalesDashboard = () => {
  const [orders, setOrders] = useState(null)
  const [markets, setMarkets] = useState([])
  const [timeRange, setTimeRange] = useState('30d') // '7d' | '30d' | '90d' | 'ytd'
  const [isError, setError] = useState(false)
  const [hoverIndex, setHoverIndex] = useState(null)

  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      try {
        const [ordersRes, marketsRes] = await Promise.all([
          API.graphql(graphqlOperation(listOrders, { limit: 999 })),
          API.graphql(graphqlOperation(listMarkets, { limit: 100 }))
        ])
        if (mounted) {
          setOrders(ordersRes.data.listOrders.items || [])
          setMarkets(marketsRes.data.listMarkets.items || [])
        }
      } catch (err) {
        console.error('Error fetching dashboard data', err)
        if (mounted) setError(true)
      }
    }
    fetchData()
    return () => { mounted = false }
  }, [])

  if (isError) return <Note>Couldn&rsquo;t load sales analytics — try again later.</Note>
  if (!orders) return <Loading fullscreen={true} text="Assembling premium merchant dashboard..." />

  // 1. Time range filter bounds
  const getStartDateLimit = () => {
    const now = new Date()
    switch (timeRange) {
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      case 'ytd':
        return new Date(now.getFullYear(), 0, 1)
      case '30d':
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    }
  }

  const startLimit = getStartDateLimit()
  const filteredOrders = orders.filter(o => new Date(o.createdAt) >= startLimit)

  // 2. Compute key metrics
  const totalOrders = filteredOrders.length
  const cancelledOrders = filteredOrders.filter(o => o.status === 'Cancelled' || o.status === 'Canceled')
  const completedOrders = filteredOrders.filter(o => o.status === 'Delivered')
  const processingOrders = filteredOrders.filter(o => o.status === 'Processing')
  const readyOrders = filteredOrders.filter(o => o.status === 'Delivery Ready')
  
  const grossSales = filteredOrders.reduce((sum, o) => sum + (o.price || 0), 0)
  const netSales = filteredOrders
    .filter(o => o.status !== 'Cancelled' && o.status !== 'Canceled')
    .reduce((sum, o) => sum + (o.price || 0), 0)
    
  const cancellationRate = totalOrders > 0 ? (cancelledOrders.length / totalOrders) * 100 : 0
  const validOrdersCount = totalOrders - cancelledOrders.length
  const avgOrderValue = validOrdersCount > 0 ? netSales / validOrdersCount : 0

  // 3. Chart points generation
  const chartPointsCount = 6
  const nowTime = new Date().getTime()
  const startTime = startLimit.getTime()
  const intervalMs = (nowTime - startTime) / chartPointsCount

  const chartData = Array.from({ length: chartPointsCount + 1 }, (_, i) => {
    const blockStart = startTime + i * intervalMs
    const blockEnd = blockStart + intervalMs
    
    const blockOrders = filteredOrders.filter(o => {
      if (o.status === 'Cancelled' || o.status === 'Canceled') return false
      const t = new Date(o.createdAt).getTime()
      return t >= blockStart && t < blockEnd
    })

    const revenue = blockOrders.reduce((sum, o) => sum + (o.price || 0), 0)
    
    return {
      label: new Date(blockStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      revenue: revenue / 100, // in dollars
      orders: blockOrders.length,
      timestamp: blockStart
    }
  })

  // 4. Category breakdown logic using market lookup
  const categoryMap = {}
  const productMarketMap = {}
  markets.forEach(m => {
    if (m.products && m.products.items) {
      m.products.items.forEach(p => {
        productMarketMap[p.description.toLowerCase().trim()] = m.name
      })
    }
  })

  filteredOrders.forEach(o => {
    if (o.status === 'Cancelled' || o.status === 'Canceled') return
    const items = (o.products && o.products.items) || []
    items.forEach(item => {
      const cat = productMarketMap[item.description.toLowerCase().trim()] || 'Groceries'
      const cost = (item.price || 0) * (item.quantity || 0)
      if (!categoryMap[cat]) {
        categoryMap[cat] = { name: cat, revenue: 0, units: 0 }
      }
      categoryMap[cat].revenue += cost
      categoryMap[cat].units += item.quantity || 0
    })
  })
  const categoriesList = Object.values(categoryMap).sort((a, b) => b.revenue - a.revenue)

  // 5. Top Selling Products
  const topProductsMap = new Map()
  filteredOrders.forEach(o => {
    if (o.status === 'Cancelled' || o.status === 'Canceled') return
    const items = (o.products && o.products.items) || []
    items.forEach(p => {
      if (!p || !p.description) return
      const key = p.description.trim().toLowerCase()
      const entry = topProductsMap.get(key) || {
        name: p.description.trim(),
        units: 0,
        revenue: 0,
        imgKey: p.file && p.file.key,
      }
      entry.units += p.quantity || 0
      entry.revenue += (p.price || 0) * (p.quantity || 0)
      topProductsMap.set(key, entry)
    })
  })
  const topProducts = [...topProductsMap.values()]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)

  // SVG Chart Geometry
  const VW = 600, VH = 180
  const M = { top: 15, right: 15, bottom: 25, left: 55 }
  const plotW = VW - M.left - M.right
  const plotH = VH - M.top - M.bottom
  const maxRevenue = Math.max(...chartData.map(d => d.revenue), 100)
  
  // Nice Ceiling
  const getCeiling = v => {
    const pow = Math.pow(10, Math.floor(Math.log10(v)))
    for (const m of [1, 2, 5, 10]) {
      if (m * pow >= v) return m * pow
    }
    return 10 * pow
  }
  const yCeil = getCeiling(maxRevenue)
  const yTicks = [0, 0.5, 1].map(f => f * yCeil)
  
  const getX = i => M.left + (i / chartPointsCount) * plotW
  const getY = val => M.top + plotH - (val / yCeil) * plotH

  // Construct Area Path
  let areaD = `M ${getX(0)} ${M.top + plotH}`
  chartData.forEach((d, i) => {
    areaD += ` L ${getX(i)} ${getY(d.revenue)}`
  })
  areaD += ` L ${getX(chartPointsCount)} ${M.top + plotH} Z`

  // Construct Stroke Path
  let strokeD = `M ${getX(0)} ${getY(chartData[0].revenue)}`
  chartData.forEach((d, i) => {
    if (i > 0) strokeD += ` L ${getX(i)} ${getY(d.revenue)}`
  })

  return (
    <Wrap>
      {/* Time range controller */}
      <FilterBar>
        <FilterTitle>Analytics Scope</FilterTitle>
        <BtnGroup>
          {['7d', '30d', '90d', 'ytd'].map(range => (
            <FilterBtn
              key={range}
              $active={timeRange === range}
              onClick={() => {
                setTimeRange(range)
                setHoverIndex(null)
              }}
            >
              {range === '7d' && 'Last 7 Days'}
              {range === '30d' && 'Last 30 Days'}
              {range === '90d' && 'Last 90 Days'}
              {range === 'ytd' && 'Year to Date'}
            </FilterBtn>
          ))}
        </BtnGroup>
      </FilterBar>

      {/* Metrics tiles */}
      <TilesGrid>
        <Tile>
          <TileIcon $color="#01bf71" $bg="rgba(1,191,113,0.1)">💰</TileIcon>
          <div>
            <TileLabel>Net Revenue</TileLabel>
            <TileValue>{dollars(netSales)}</TileValue>
            <TileSub>Gross: {dollars(grossSales)}</TileSub>
          </div>
        </Tile>
        <Tile>
          <TileIcon $color="#ff9a3c" $bg="rgba(255,154,60,0.1)">🧾</TileIcon>
          <div>
            <TileLabel>Orders Placed</TileLabel>
            <TileValue>{formatNumber(totalOrders)}</TileValue>
            <TileSub>Active: {formatNumber(processingOrders.length + readyOrders.length)}</TileSub>
          </div>
        </Tile>
        <Tile>
          <TileIcon $color="#0a8f57" $bg="rgba(10,143,87,0.1)">🛍️</TileIcon>
          <div>
            <TileLabel>Average Basket (AOV)</TileLabel>
            <TileValue>{dollars(avgOrderValue)}</TileValue>
            <TileSub>Per valid transaction</TileSub>
          </div>
        </Tile>
        <Tile>
          <TileIcon $color="#ef5350" $bg="rgba(239,83,80,0.1)">❌</TileIcon>
          <div>
            <TileLabel>Cancellations</TileLabel>
            <TileValue>{cancellationRate.toFixed(1)}%</TileValue>
            <TileSub>{cancelledOrders.length} orders cancelled</TileSub>
          </div>
        </Tile>
      </TilesGrid>

      {/* Analytics Visualization sections */}
      <DoubleColumn>
        {/* SVG Area spline chart */}
        <AnalyticsCard>
          <CardHeader>
            <div>
              <CardTitle>Sales Trend Spline</CardTitle>
              <CardSub>Net sales trajectory over selected timeframe</CardSub>
            </div>
          </CardHeader>

          <ChartWrapper>
            <svg viewBox={`0 0 ${VW} ${VH}`} width="100%">
              <defs>
                <linearGradient id="chart-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#01bf71" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#01bf71" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid ticks */}
              {yTicks.map(t => (
                <g key={t}>
                  <line
                    x1={M.left}
                    x2={VW - M.right}
                    y1={getY(t)}
                    y2={getY(t)}
                    stroke="rgba(31, 45, 61, 0.05)"
                    strokeWidth="1.2"
                  />
                  <text
                    x={M.left - 10}
                    y={getY(t) + 4}
                    textAnchor="end"
                    fontSize="10"
                    fill="#8a99a8"
                    fontWeight="700"
                  >
                    ${t.toFixed(0)}
                  </text>
                </g>
              ))}

              {/* Shaded Area fill */}
              <path d={areaD} fill="url(#chart-gradient)" />

              {/* Bold Path stroke */}
              <path d={strokeD} fill="none" stroke="#01bf71" strokeWidth="3" strokeLinecap="round" />

              {/* Interactive hovering tracking */}
              {chartData.map((d, i) => (
                <g key={i}>
                  <circle
                    cx={getX(i)}
                    cy={getY(d.revenue)}
                    r={hoverIndex === i ? 6 : 4}
                    fill={hoverIndex === i ? '#01bf71' : '#fff'}
                    stroke="#01bf71"
                    strokeWidth="2.5"
                    style={{ transition: 'all 0.15s ease' }}
                  />
                  {/* Invisible pointer & touch overlay triggers */}
                  <rect
                    x={getX(i) - 20}
                    y={M.top}
                    width="40"
                    height={plotH}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onTouchStart={() => setHoverIndex(i)}
                    onTouchMove={() => setHoverIndex(i)}
                  />
                </g>
              ))}
            </svg>

            {hoverIndex !== null && (
              <ChartTooltip style={{ left: `${(getX(hoverIndex) / VW) * 100}%` }}>
                <TooltipTitle>Date: {chartData[hoverIndex].label}</TooltipTitle>
                <TooltipMetric>Revenue: ${chartData[hoverIndex].revenue.toFixed(2)}</TooltipMetric>
                <TooltipSub>{chartData[hoverIndex].orders} Orders placed</TooltipSub>
              </ChartTooltip>
            )}
          </ChartWrapper>
        </AnalyticsCard>

        {/* Categories Distribution */}
        <AnalyticsCard>
          <CardHeader>
            <div>
              <CardTitle>Market Segments</CardTitle>
              <CardSub>Sales volume & revenue by category</CardSub>
            </div>
          </CardHeader>

          {categoriesList.length === 0 ? (
            <EmptyContent>No category transactions recorded yet.</EmptyContent>
          ) : (
            <CategoryScroll>
              {categoriesList.map((cat, idx) => {
                const colors = ['#01bf71', '#ff9a3c', '#0a8f57', '#a0522d', '#9932cc'];
                const color = colors[idx % colors.length];
                const percentage = grossSales > 0 ? (cat.revenue / grossSales) * 100 : 0;
                
                return (
                  <CategoryRow key={cat.name}>
                    <CategoryColor $color={color} />
                    <CategoryInfo>
                      <CategoryName>{cat.name}</CategoryName>
                      <CategoryMeta>{cat.units} units sold</CategoryMeta>
                    </CategoryInfo>
                    <CategoryStats>
                      <CategoryRevenue>${(cat.revenue / 100).toFixed(2)}</CategoryRevenue>
                      <CategoryPercent>{percentage.toFixed(0)}% share</CategoryPercent>
                    </CategoryStats>
                  </CategoryRow>
                );
              })}
            </CategoryScroll>
          )}
        </AnalyticsCard>
      </DoubleColumn>

      {/* Fulfillment progress & Top products */}
      <DoubleColumn>
        {/* Fulfillment list */}
        <AnalyticsCard>
          <CardHeader>
            <div>
              <CardTitle>Delivery & Fulfillment</CardTitle>
              <CardSub>Overview of active logistics states</CardSub>
            </div>
          </CardHeader>

          <FulfillmentGrid>
            <FulfillCard $bg="rgba(1,191,113,0.06)" $border="rgba(1,191,113,0.15)">
              <FulfillLabel>Processing</FulfillLabel>
              <FulfillCount>{processingOrders.length}</FulfillCount>
              <FulfillStatus>📥 Awaiting packaging</FulfillStatus>
            </FulfillCard>
            <FulfillCard $bg="rgba(255,154,60,0.06)" $border="rgba(255,154,60,0.15)">
              <FulfillLabel>Delivery Ready</FulfillLabel>
              <FulfillCount>{readyOrders.length}</FulfillCount>
              <FulfillStatus>🚚 In transit / dispatch</FulfillStatus>
            </FulfillCard>
            <FulfillCard $bg="rgba(10,143,87,0.06)" $border="rgba(10,143,87,0.15)">
              <FulfillLabel>Completed</FulfillLabel>
              <FulfillCount>{completedOrders.length}</FulfillCount>
              <FulfillStatus>📦 Delivered successfully</FulfillStatus>
            </FulfillCard>
            <FulfillCard $bg="rgba(239,83,80,0.06)" $border="rgba(239,83,80,0.15)">
              <FulfillLabel>Cancelled</FulfillLabel>
              <FulfillCount>{cancelledOrders.length}</FulfillCount>
              <FulfillStatus>✕ Voided transactions</FulfillStatus>
            </FulfillCard>
          </FulfillmentGrid>
        </AnalyticsCard>

        {/* Top selling items */}
        <AnalyticsCard>
          <CardHeader>
            <div>
              <CardTitle>Top Grocery Items</CardTitle>
              <CardSub>Top 5 products by revenue share</CardSub>
            </div>
          </CardHeader>

          {topProducts.length === 0 ? (
            <EmptyContent>No product sales recorded.</EmptyContent>
          ) : (
            <ProductList>
              {topProducts.map((p, i) => (
                <ProductRow key={p.name}>
                  <ProductIndex>{i + 1}</ProductIndex>
                  {p.imgKey ? (
                    <ProductThumb
                      src={`https://${bucket}.s3.${region}.amazonaws.com/public/${p.imgKey}`}
                      alt=""
                    />
                  ) : (
                    <ProductThumbPlaceholder />
                  )}
                  <ProductDetails>
                    <ProductName title={p.name}>{p.name}</ProductName>
                    <ProductUnits>{p.units} packages sold</ProductUnits>
                  </ProductDetails>
                  <ProductRevenue>${(p.revenue / 100).toFixed(2)}</ProductRevenue>
                </ProductRow>
              ))}
            </ProductList>
          )}
        </AnalyticsCard>
      </DoubleColumn>
    </Wrap>
  )
}

export default SalesDashboard

/* ---------- styled components ---------- */
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 14px 20px;
  box-shadow: 0 4px 12px rgba(31, 45, 61, 0.03);

  @media screen and (min-width: 600px) {
    flex-direction: row;
    align-items: center;
  }
`;

const FilterTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  color: #1f2d3d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BtnGroup = styled.div`
  display: flex;
  background: rgba(31, 45, 61, 0.06);
  border-radius: 999px;
  padding: 3px;
`;

const FilterBtn = styled.button`
  border: none;
  background: ${props => (props.$active ? '#fff' : 'transparent')};
  color: ${props => (props.$active ? '#0a8f57' : '#6a7b8a')};
  font-weight: 700;
  font-size: 0.82rem;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: ${props => (props.$active ? '0 2px 8px rgba(31, 45, 61, 0.1)' : 'none')};
  transition: all 0.15s ease;

  &:hover {
    color: #0a8f57;
  }
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const Tile = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 14px rgba(31, 45, 61, 0.03);
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TileIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 1.4rem;
  display: grid;
  place-items: center;
  color: ${props => props.$color};
  background: ${props => props.$bg};
`;

const TileLabel = styled.div`
  font-size: 0.76rem;
  font-weight: 700;
  color: #8a99a8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
`;

const TileValue = styled.div`
  font-size: 1.45rem;
  font-weight: 800;
  color: #1f2d3d;
  line-height: 1.15;
`;

const TileSub = styled.div`
  font-size: 0.8rem;
  color: #6a7b8a;
  margin-top: 3px;
`;

const DoubleColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: 820px) {
    grid-template-columns: 1.2fr 0.8fr;
  }
`;

const AnalyticsCard = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.03);
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  margin: 0 0 2px;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1f2d3d;
`;

const CardSub = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #8a99a8;
`;

const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ChartTooltip = styled.div`
  position: absolute;
  top: 10px;
  transform: translateX(-50%);
  background: #1f2d3d;
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  pointer-events: none;
  font-size: 0.78rem;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.25);
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 130px;
  z-index: 10;
  transition: left 0.12s ease-out;

  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px 6px 0;
    border-style: solid;
    border-color: #1f2d3d transparent;
  }
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  color: #c9d2da;
`;

const TooltipMetric = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  color: #01bf71;
`;

const TooltipSub = styled.div`
  font-size: 0.72rem;
  color: #8a99a8;
`;

const CategoryScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 180px;
  overflow-y: auto;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryColor = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const CategoryInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategoryName = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CategoryMeta = styled.div`
  font-size: 0.76rem;
  color: #8a99a8;
`;

const CategoryStats = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const CategoryRevenue = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2d3d;
`;

const CategoryPercent = styled.div`
  font-size: 0.76rem;
  color: #0a8f57;
  font-weight: 700;
`;

const FulfillmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FulfillCard = styled.div`
  background: ${props => props.$bg};
  border: 1px solid ${props => props.$border};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FulfillLabel = styled.div`
  font-size: 0.76rem;
  font-weight: 700;
  color: #6a7b8a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FulfillCount = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: #1f2d3d;
  line-height: 1;
`;

const FulfillStatus = styled.div`
  font-size: 0.78rem;
  color: #8a99a8;
  margin-top: 2px;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProductIndex = styled.span`
  font-size: 0.8rem;
  font-weight: 800;
  color: #8a99a8;
  width: 16px;
  text-align: center;
`;

const ProductThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  background: #f7f9fa;
`;

const ProductThumbPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #f0f3f5;
`;

const ProductDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`;

const ProductUnits = styled.div`
  font-size: 0.76rem;
  color: #8a99a8;
`;

const ProductRevenue = styled.span`
  font-size: 0.9rem;
  font-weight: 800;
  color: #0a8f57;
`;

const EmptyContent = styled.div`
  padding: 30px;
  text-align: center;
  color: #8a99a8;
  font-size: 0.88rem;
`;

const Note = styled.div`
  padding: 30px;
  text-align: center;
  color: #ef5350;
  font-weight: 700;
`;
